import type { AdminLeadDuplicateSummary } from "./crm-duplicates";
import type {
  AdminLeadGuestConversionPreflightRevision,
  AdminLeadGuestConversionRecord,
} from "./crm-guest-conversion";

export type ContactId = string & { readonly __brand: "ContactId" };
export type LeadId = string & { readonly __brand: "LeadId" };
export type LeadSourceId = string & { readonly __brand: "LeadSourceId" };
export type LeadStageTransitionId = string & {
  readonly __brand: "LeadStageTransitionId";
};
export type LeadAssignmentTransitionId = string & {
  readonly __brand: "LeadAssignmentTransitionId";
};
export type LeadPipelineRevision = number & {
  readonly __brand: "LeadPipelineRevision";
};
export type LeadAssignmentRevision = number & {
  readonly __brand: "LeadAssignmentRevision";
};
export type AdminLeadListCursor = string & {
  readonly __brand: "AdminLeadListCursor";
};
export type AdminLeadPipelineCursor = string & {
  readonly __brand: "AdminLeadPipelineCursor";
};
export type AdminLeadStageHistoryCursor = string & {
  readonly __brand: "AdminLeadStageHistoryCursor";
};
export type AdminLeadAssigneeDirectoryCursor = string & {
  readonly __brand: "AdminLeadAssigneeDirectoryCursor";
};

export const crmRecordStatuses = ["ACTIVE", "ARCHIVED"] as const;
export type CrmRecordStatus = (typeof crmRecordStatuses)[number];

export const leadPipelineStages = [
  "NEW",
  "CONTACTED",
  "QUALIFIED",
  "APPOINTMENT_HELD",
  "BOOKED",
  "VISITED",
  "FOLLOW_UP",
  "WON",
  "LOST",
  "DO_NOT_CONTACT",
] as const;
export type LeadPipelineStage = (typeof leadPipelineStages)[number];

export const leadAssignableRoles = ["ADMIN", "CRM_AGENT"] as const;
export type LeadAssignableRole = (typeof leadAssignableRoles)[number];

export const leadSourceKinds = [
  "DIRECT",
  "ORGANIC_SEARCH",
  "REFERRAL",
  "CAMPAIGN",
  "CHATGPT",
  "UNKNOWN",
] as const;
export type LeadSourceKind = (typeof leadSourceKinds)[number];

export const adminLeadListPageSizes = [25, 50, 100] as const;
export type AdminLeadListPageSize = (typeof adminLeadListPageSizes)[number];
export const adminLeadPipelinePageSizes = [25] as const;
export type AdminLeadPipelinePageSize =
  (typeof adminLeadPipelinePageSizes)[number];
export const adminLeadStageHistoryPageSizes = [25] as const;
export type AdminLeadStageHistoryPageSize =
  (typeof adminLeadStageHistoryPageSizes)[number];
export const adminLeadAssigneeDirectoryPageSizes = [50] as const;
export type AdminLeadAssigneeDirectoryPageSize =
  (typeof adminLeadAssigneeDirectoryPageSizes)[number];

export interface ContactRecord {
  readonly id: ContactId;
  readonly organizationId: string;
  readonly displayName: string;
  readonly emailNormalized: string | null;
  readonly phoneE164: string | null;
  readonly recordStatus: CrmRecordStatus;
  readonly archivedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface LeadRecord {
  readonly id: LeadId;
  readonly organizationId: string;
  readonly contactId: ContactId;
  readonly pipelineStage: LeadPipelineStage;
  readonly pipelineRevision: LeadPipelineRevision;
  readonly assignedUserId: string | null;
  readonly assignmentRevision: LeadAssignmentRevision;
  readonly recordStatus: CrmRecordStatus;
  readonly archivedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface LeadStageTransitionRecord {
  readonly id: LeadStageTransitionId;
  readonly organizationId: string;
  readonly leadId: LeadId;
  readonly fromStage: LeadPipelineStage;
  readonly toStage: LeadPipelineStage;
  readonly pipelineRevision: LeadPipelineRevision;
  readonly expectedUpdatedAt: string | null;
  readonly actorUserId: string;
  readonly correlationId: string;
  readonly idempotencyKeyHash: string;
  readonly occurredAt: string;
  readonly createdAt: string;
}

export interface LeadAssignmentTransitionRecord {
  readonly id: LeadAssignmentTransitionId;
  readonly organizationId: string;
  readonly leadId: LeadId;
  readonly fromAssigneeUserId: string | null;
  readonly toAssigneeUserId: string | null;
  readonly assignmentRevision: LeadAssignmentRevision;
  readonly expectedUpdatedAt: string;
  readonly actorUserId: string;
  readonly correlationId: string;
  readonly idempotencyKeyHash: string;
  readonly occurredAt: string;
  readonly createdAt: string;
}

export interface LeadSourceRecord {
  readonly id: LeadSourceId;
  readonly organizationId: string;
  readonly leadId: LeadId;
  readonly sourceKind: LeadSourceKind;
  readonly sourceKey: string | null;
  readonly campaignKey: string | null;
  readonly utmSource: string | null;
  readonly utmMedium: string | null;
  readonly utmCampaign: string | null;
  readonly utmTerm: string | null;
  readonly utmContent: string | null;
  readonly referrerOrigin: string | null;
  readonly landingPagePath: string | null;
  readonly capturedAt: string;
  readonly createdAt: string;
}

export interface AdminLeadAssignee {
  readonly userId: string;
  readonly displayLabel: string;
  readonly role: LeadAssignableRole;
}

export interface AdminLeadListItem {
  readonly leadId: LeadId;
  readonly displayName: string;
  readonly emailNormalized: string | null;
  readonly phoneE164: string | null;
  readonly pipelineStage: LeadPipelineStage;
  readonly leadRecordStatus: CrmRecordStatus;
  readonly contactRecordStatus: CrmRecordStatus;
  readonly sourceKind: LeadSourceKind | null;
  readonly sourceKey: string | null;
  readonly campaignKey: string | null;
  readonly assignedUserId: string | null;
  readonly assignedTo: AdminLeadAssignee | null;
  readonly assignmentRevision: LeadAssignmentRevision;
  readonly duplicateSummary: AdminLeadDuplicateSummary;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface AdminLeadListQuery {
  readonly cursor: AdminLeadListCursor | null;
  readonly limit: AdminLeadListPageSize;
}

export interface AdminLeadListPage {
  readonly items: readonly AdminLeadListItem[];
  readonly nextCursor: AdminLeadListCursor | null;
}

export interface AdminLeadDetail {
  readonly summary: AdminLeadListItem;
  readonly leadArchivedAt: string | null;
  readonly sourceCapturedAt: string | null;
  readonly guestConversion: AdminLeadGuestConversionRecord | null;
  readonly guestConversionPreflightRevision: AdminLeadGuestConversionPreflightRevision | null;
}

export interface AdminLeadDetailQuery {
  readonly leadId: LeadId;
}

export interface AdminLeadPipelineItem {
  readonly leadId: LeadId;
  readonly displayName: string;
  readonly pipelineStage: LeadPipelineStage;
  readonly pipelineRevision: LeadPipelineRevision;
  readonly sourceKind: LeadSourceKind | null;
  readonly sourceKey: string | null;
  readonly assignedUserId: string | null;
  readonly assignedTo: AdminLeadAssignee | null;
  readonly assignmentRevision: LeadAssignmentRevision;
  readonly duplicateSummary: AdminLeadDuplicateSummary;
  readonly updatedAt: string;
}

export interface AdminLeadPipelineLaneQuery {
  readonly pipelineStage: LeadPipelineStage;
  readonly cursor: AdminLeadPipelineCursor | null;
  readonly limit: AdminLeadPipelinePageSize;
}

export interface AdminLeadPipelineLanePage {
  readonly pipelineStage: LeadPipelineStage;
  readonly items: readonly AdminLeadPipelineItem[];
  readonly nextCursor: AdminLeadPipelineCursor | null;
}

export interface AdminLeadPipelineMoveRequest {
  readonly leadId: LeadId;
  readonly expectedStage: LeadPipelineStage;
  readonly expectedPipelineRevision: LeadPipelineRevision;
  readonly expectedUpdatedAt: string;
  readonly targetStage: LeadPipelineStage;
}

export interface AdminLeadStageHistoryItem {
  readonly fromStage: LeadPipelineStage;
  readonly toStage: LeadPipelineStage;
  readonly pipelineRevision: LeadPipelineRevision;
  readonly occurredAt: string;
}

export interface AdminLeadStageHistoryQuery {
  readonly leadId: LeadId;
  readonly cursor: AdminLeadStageHistoryCursor | null;
  readonly limit: AdminLeadStageHistoryPageSize;
}

export interface AdminLeadStageHistoryPage {
  readonly items: readonly AdminLeadStageHistoryItem[];
  readonly nextCursor: AdminLeadStageHistoryCursor | null;
}

export type AdminLeadPipelineMoveOutcome =
  | {
      readonly kind: "confirmed";
      readonly item: AdminLeadPipelineItem;
      readonly transition: AdminLeadStageHistoryItem;
    }
  | { readonly kind: "conflict" }
  | { readonly kind: "rejected" };

export interface AdminLeadAssigneeDirectoryQuery {
  readonly cursor: AdminLeadAssigneeDirectoryCursor | null;
  readonly limit: AdminLeadAssigneeDirectoryPageSize;
}

export interface AdminLeadAssigneeDirectoryPage {
  readonly items: readonly AdminLeadAssignee[];
  readonly nextCursor: AdminLeadAssigneeDirectoryCursor | null;
}

export interface AdminLeadAssignmentRequest {
  readonly leadId: LeadId;
  readonly expectedAssigneeUserId: string | null;
  readonly expectedAssignmentRevision: LeadAssignmentRevision;
  readonly expectedUpdatedAt: string;
  readonly targetAssigneeUserId: string | null;
}

export interface AdminLeadAssignmentState {
  readonly leadId: LeadId;
  readonly assignedUserId: string | null;
  readonly assignedTo: AdminLeadAssignee | null;
  readonly assignmentRevision: LeadAssignmentRevision;
  readonly updatedAt: string;
}

export interface AdminLeadAssignmentHistoryItem {
  readonly fromAssigneeUserId: string | null;
  readonly toAssigneeUserId: string | null;
  readonly assignmentRevision: LeadAssignmentRevision;
  readonly occurredAt: string;
}

export type AdminLeadAssignmentOutcome =
  | {
      readonly kind: "confirmed";
      readonly state: AdminLeadAssignmentState;
      readonly transition: AdminLeadAssignmentHistoryItem;
    }
  | { readonly kind: "conflict" }
  | { readonly kind: "rejected" };
